import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { calenderEndpoints } from '../../services/api/endpoints/calendar.endpoints';

interface CalendarProvider {
  name: string;
  isGoogleCalendarEnabled: boolean;
  isOutlookCalendarEnabled: boolean;
  isAppleCalendarEnabled: boolean;
  googleMail: string;
  outlookMail: string;
  appleMail: string;
}

interface CalendarState {
  calendarProvider: CalendarProvider | null; // Change from array to single object
  isLoading: boolean;
  error: string | null;
}

const initialState: CalendarState = {
  calendarProvider: null, // Change from `[]` to `null`
  isLoading: false,
  error: null,
};

export const fetchCalendarProviders = createAsyncThunk(
  'calendar/fetchCalendarProviders',
  async (
    { organizationId, resourceId }: { organizationId: string; resourceId: number },
    { rejectWithValue }: { rejectWithValue: (value: string) => void }
  ) => {
    try {
      const response = await calenderEndpoints.getResourceCalendarProviders(
        organizationId,
        resourceId
      );
      return response.data; // Make sure we're returning the object directly
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch calendar providers');
    }
  }
);

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    clearCalendarProvider(state) {
      state.calendarProvider = null;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCalendarProviders.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCalendarProviders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.calendarProvider = action.payload; // Store as an object, not an array
      })
      .addCase(fetchCalendarProviders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCalendarProvider } = calendarSlice.actions;
export const selectCalendar = (state: RootState) => state.calendar;
export default calendarSlice.reducer;
